import React, { useEffect, useRef, useState } from 'react';
import { Image, Mic, Send, Settings } from 'lucide-react';
import { useAdminStatus } from '../../../hooks/queries/useAuth';
import { ModelSelector } from './ModelSelector';
import { TopicInput, InputButton } from '../../shared/TopicInput';
import { cn } from '../../../lib/utils';

interface ChatInputProps {
  input: string;
  isRecording: boolean;
  onInputChange: (value: string) => void;
  onSubmit: (e: React.FormEvent, model?: string) => void;
  onVoiceClick: () => void;
  isTyping: boolean;
  botName: string;
  onImageClick?: () => void;
}

const LOCAL_STORAGE_MODEL_KEY = 'zentime_selected_ai_model';

export function ChatInput({
  input,
  isRecording,
  onInputChange,
  onSubmit,
  onVoiceClick,
  isTyping,
  botName,
  onImageClick,
}: ChatInputProps) {
  const modelSelectorButtonRef = useRef<HTMLButtonElement>(null);
  const { data: isAdmin = false } = useAdminStatus();
  const [modelSelectorOpen, setModelSelectorOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState<string | null>(null);

  // Load previously selected model from localStorage on init
  useEffect(() => {
    const savedModel = localStorage.getItem(LOCAL_STORAGE_MODEL_KEY);
    if (savedModel) {
      setSelectedModel(savedModel);
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(e, selectedModel || undefined);
  };

  const handleModelSelect = (modelId: string) => {
    setSelectedModel(modelId);
    setModelSelectorOpen(false);

    // Save selected model to localStorage for persistence
    localStorage.setItem(LOCAL_STORAGE_MODEL_KEY, modelId);
  };

  // Create buttons array for TopicInput
  const buttons: InputButton[] = [];

  // Model selector button - only for admins
  if (isAdmin) {
    buttons.push({
      id: 'model-selector',
      icon: <Settings size={20} className={selectedModel ? 'text-purple-600' : ''} />,
      onClick: () => setModelSelectorOpen(!modelSelectorOpen),
      title: 'Select AI model',
      disabled: isTyping,
      color: selectedModel ? 'purple' : 'blue',
      showWhenEmpty: true,
      hideWhenRecording: true,
    });
  }

  // Voice recording button
  buttons.push({
    id: 'voice-recording',
    icon: <Mic size={20} className={cn(isRecording && 'animate-pulse')} />,
    onClick: onVoiceClick,
    title: isRecording ? 'Stop recording' : 'Start recording',
    disabled: isTyping,
    isActive: isRecording,
    color: 'blue',
    showWhenEmpty: true,
  });

  // Image upload button
  if (onImageClick) {
    buttons.push({
      id: 'image-upload',
      icon: <Image size={20} />,
      onClick: onImageClick,
      title: 'Upload image',
      disabled: isTyping,
      color: 'blue',
      showWhenEmpty: true,
      hideWhenRecording: true,
    });
  }

  // Send button
  buttons.push({
    id: 'send',
    icon: <Send size={20} />,
    onClick: () => {}, // Will use form's submit handler instead
    title: 'Send message',
    disabled: !input.trim() || isTyping,
    color: selectedModel ? 'purple' : 'blue',
    isSubmitButton: true,
    hideWhenRecording: true,
  });

  return (
    <div className="relative">
      <TopicInput
        input={input}
        setInput={onInputChange}
        placeholder={isTyping ? `${botName} is typing...` : 'Your message...'}
        isRecording={isRecording}
        isTyping={isTyping}
        onSubmit={handleSubmit}
        buttons={buttons}
        extraInfo={
          selectedModel ? (
            <div className="text-xs text-purple-600 font-medium">Using model: {selectedModel}</div>
          ) : null
        }
      />

      {/* Model selector popup */}
      <ModelSelector
        onSelect={handleModelSelect}
        onClose={() => setModelSelectorOpen(false)}
        isOpen={modelSelectorOpen}
        triggerRef={modelSelectorButtonRef}
      />
    </div>
  );
}
